import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { loadGlobalData, saveGlobalData } from '../component/action/preferences.jsx';
import useAuthenticate from '../actions/Authenticate.jsx';

const GlobalDataContext = createContext(null);

/**
 * Applies body background from global display config to document.
 */
const applyBodyStyles = (data) => {
  try {
    if (typeof document === 'undefined' || !data) return;
    const display = data?.display ?? {};
    const bodyBg = display?.body?.body_background_image;
    if (bodyBg) {
      document.documentElement.style.setProperty(
        '--body-background-image',
        `url(${bodyBg})`
      );
    }
  } catch (error) {
    console.error('Failed to apply body styles:', error);
  }
};

export function GlobalDataProvider({ children }) {
  const [globalData, setGlobalDataState] = useState(null);
  const hasCalledGlobal = useRef(false);

  // Load once from preferences on mount
  useEffect(() => {
    const initFromPreferences = async () => {
      try {
        const value = await loadGlobalData();
        if (!value) return;
        let parsed = value;
        try {
          parsed = JSON.parse(value);
        } catch {
          return;
        }
        setGlobalDataState(parsed);
        // applyBodyStyles(parsed);
      } catch (error) {
        console.error('Error loading global data from preferences:', error);
      }
    };
    initFromPreferences();
  }, []);

  // Apply body styles whenever globalData changes (e.g. after API update)
  // useEffect(() => {
  // if (globalData) applyBodyStyles(globalData);
  // }, [globalData]);

  const setGlobalData = (data) => {
    setGlobalDataState(data);
  };

  return (
    <GlobalDataContext.Provider value={{ globalData, setGlobalData }}>
      <GlobalDataSync />
      {children}
    </GlobalDataContext.Provider>
  );
}

/**
 * Fetches global config from API once and updates context + preferences.
 * Must be rendered inside GlobalDataProvider.
 */
function GlobalDataSync() {
  const { setGlobalData } = useContext(GlobalDataContext);
  const { global: fetchGlobal } = useAuthenticate();
  const hasCalledGlobal = useRef(false);

  useEffect(() => {
    if (hasCalledGlobal.current) return;
    hasCalledGlobal.current = true;

    const fetchGlobalData = async () => {
      try {
        const response = await fetchGlobal();
        if (response !== null) {
          try {
            await saveGlobalData(response);
            setGlobalData(response);
          } catch (storageError) {
            console.error('Failed to persist global data:', storageError);
          }
        } else {
          console.error('Failed to fetch global data:', response?.message);
        }
      } catch (error) {
        console.error('Error calling global API:', error);
      }
    };

    fetchGlobalData();
  }, [fetchGlobal, setGlobalData]);

  return null;
}

export function useGlobalData() {
  const context = useContext(GlobalDataContext);
  if (!context) {
    throw new Error('useGlobalData must be used within a GlobalDataProvider');
  }
  return context;
}
